import { memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Modal, message } from 'antd';

import { useMigrateMutation } from '../../api/plan-migration.api';
import type { MigrationStatusResponse } from '../../model/types/plan-migration';
import { PlanSelector } from '../plan-selector/plan-selector';

import cls from './plan-migration-popup.module.css';

interface PlanMigrationPopupProps {
    isOpen: boolean;
    migrationData: MigrationStatusResponse;
}

export const PlanMigrationPopup = memo((props: PlanMigrationPopupProps) => {
    const { isOpen, migrationData } = props;
    const { t } = useTranslation('subscription');

    const [migrate, { isLoading }] = useMigrateMutation();
    const [selectedPriceId, setSelectedPriceId] = useState<string | null>(null);

    const handleSelectPlan = useCallback(
        async (priceId: string) => {
            setSelectedPriceId(priceId);

            try {
                const result = await migrate({
                    stripe_price_id: priceId,
                }).unwrap();

                window.location.href = result.checkout_url;
            } catch {
                setSelectedPriceId(null);
                message.error(t('migration.error'));
            }
        },
        [migrate, t],
    );

    const getTitle = () => {
        if (migrationData.current_plan === 'inactive') {
            return t('migration.reactivateTitle');
        }

        if (migrationData.current_plan === 'none') {
            return t('migration.choosePlanTitle');
        }

        return t('migration.upgradeTitle');
    };

    const getSubtitle = () => {
        if (migrationData.current_plan === 'inactive') {
            return t('migration.reactivateSubtitle');
        }

        if (migrationData.current_plan === 'none') {
            return t('migration.choosePlanSubtitle');
        }

        return t('migration.upgradeSubtitle');
    };

    const title = getTitle();
    const subtitle = getSubtitle();

    return (
        <Modal
            centered
            className={cls.modal}
            closable={false}
            footer={null}
            maskClosable={false}
            open={isOpen}
            width="100%"
        >
            <PlanSelector
                isLoading={isLoading}
                migrationData={migrationData}
                onSelectPlan={handleSelectPlan}
                selectedPriceId={selectedPriceId}
                subtitle={subtitle}
                title={title}
            />
        </Modal>
    );
});

PlanMigrationPopup.displayName = 'PlanMigrationPopup';
